// ================================================================================================
import _ from "lodash";
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { DashboardHelper } from './DashboardStructs';
import type { DashboardConfiguration } from '@ModelViews/ModelViews';
import "./BoardTheming.less";
// ================================================================================================
export interface ToolbarTheme {
    /**
     * Define the border shadow level (material-ui feeling)
     * Shadow level 0 for no shadow.
     */
    shadowLevel: number;

    /**
     * Background color
     */
    backgroundColor: string;

    /**
     * Sub-Background color
     * Helpful when we have secondary toolbar in the same context. (e.x. search bar)
     */
    subBackgroundColor: string;

    /**
     * Font Color
     */
    fontColor: string;
}
// ------------------------------------------------------------------------------------------------
export interface BoardTheme {
    // Board grid
    boardGrid: {
        backgroundColor: string;
        categoryToolbar: {
            height: number;
            style: ToolbarTheme;
        }
    }

    // Generic Toolbars
    genericToolbars: {
        height: number;
        style: ToolbarTheme;
    }

    // App Bar
    appToolbar: {
        height: number;
        style: ToolbarTheme;
    }

    // Board toolbar
    boardToolbar: {
        height: number;
        fontSize: number;
        style: ToolbarTheme;
    }

    // Page list
    pageList: {
        width: number;
        pageSelectionColor: string;
        toolbarBackgroundColor: string;
        toolbarFontColor: string;
        style: ToolbarTheme
    }

    // Dialog list
    dialog: {
        height: number; // Toolbar height
        toolbarBackgroundColor: string;
        toolbarFontColor: string;
        backgroundColor: string;
        fontColor: string;
        shadowLevel: number;
        borderRadius: number;
    }

    // Drawer 
    drawer: {
        toolbarBackgroundColor: string;
        toolbarFontColor: string;
        toolbarShadowLevel: number;
        shadowLevel: number;
    }

    // Page overrides
    page: {
        backgroundColor: string;
        widgetBackgroundColor: string;
        zDepth: number;
        borderRadius: number;
    }

    // Theming for gem user dialog box 
    gemUserDialogBox: {
        bodyBackgroundColor: string;
        toolbarBackgroundColor: string;
        toolbarFontColor: string;
        toolbarShadowLevel: number;
        acceptBackgroundColor: string;
        acceptFontColor: string;
        cancelBackgroundColor: string;
        cancelFontColor: string;
    }

    // Widgets
    widgets: {

        // Tree Widget 
        tree: {
            toolbarBackgroundColor: string;
            toolbarFontColor: string;
            toolbarShadowLevel: number;

            searchbarBackgroundColor: string;
            searchbarFontColor: string;
            searchbarShadowLevel: number;

            treeBackgroundColor: string;
            treeFontColor: string;
            treeSelectColor: string;
            treeDropdownTextIconColor: string;
        }

        // Gauges Widget
        radialGauge: {
            // All Colors
            colorPlate: string,
            colorPlateEnd: string,
            colorMajorTicks: string,
            colorMinorTicks: string,
            colorStrokeTicks: string,
            colorTitle: string,
            colorUnits: string,
            colorNumbers: string,
            colorNeedle: string,
            colorNeedleEnd: string,
            colorValueText: string,
            colorValueTextShadow: string,
            colorBorderShadow: string,
            colorBorderOuter: string,
            colorBorderOuterEnd: string,
            colorBorderMiddle: string,
            colorBorderMiddleEnd: string,
            colorBorderInner: string,
            colorBorderInnerEnd: string,
            colorValueBoxRect: string,
            colorValueBoxRectEnd: string,
            colorValueBoxBackground: string,
            colorValueBoxShadow: string,
            colorNeedleShadowUp: string,
            colorNeedleShadowDown: string,
            colorBarStroke: string,
            colorBar: string,
            colorBarProgress: string,
            colorBarShadow: string,

            // Needle
            needleShadow: boolean,
            needleType: string,
            needleStart: number,
            needleEnd: number,
            needleWidth: number
        },

        charts: {
            excelIcon: string;
        },

        table: {
            excelIcon: string;
        }

        settingsList: {
            toolbar: {
                height: number;
                backgroundColor: string;
                iconColor: string;
                shadowLevel: number;
            }
        }
    }
}
// ================================================================================================
export function getThemeSettingsList(): SettingsGroup<BoardTheme>[] {
    let groupStyle = {
        borderRadius: 15,
        backgroundColor: "#3f51b5",
        shadowLevel: 1,
        fontColor: "white"
    };
    let subGroupStyle = {
        borderRadius: 15,
        backgroundColor: "#303c82",
        shadowLevel: 1,
        fontColor: "white"
    };

    return [
        {
            name: "App Toolbar",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Slider,
                    label: "Height",
                    dataPath: "appToolbar.height",
                    minValue: 20,
                    maxValue: 60,
                    stepValue: 5,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.Slider,
                    label: "Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "appToolbar.style.shadowLevel",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Background Color",
                    dataPath: "appToolbar.style.backgroundColor",
                },/*
                {
                    type: SettingsType.ColorPicker,
                    tooltip: "Sub-Background color. Helpful when we have secondary toolbar in the same context. (e.x. search bar)",
                    label: "SubBackground Color",
                    dataPath: "appToolbar.style.subBackgroundColor",
                },*/
                {
                    type: SettingsType.ColorPicker,
                    label: "Font Color",
                    dataPath: "appToolbar.style.fontColor",
                }
            ]
        },
        {
            name: "Board Toolbar",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Slider,
                    label: "Height",
                    dataPath: "boardToolbar.height",
                    minValue: 20,
                    maxValue: 60,
                    stepValue: 5,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.Slider,
                    label: "Button font icon size",
                    tooltip: "Define the font size of icons",
                    dataPath: "boardToolbar.fontSize",
                    minValue: 10,
                    maxValue: 40,
                    stepValue: 1,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.Slider,
                    label: "Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "boardToolbar.style.shadowLevel",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Background Color",
                    dataPath: "boardToolbar.style.backgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    tooltip: "Sub-Background color. Helpful when we have secondary toolbar in the same context. (e.x. search bar)",
                    label: "SubBackground Color",
                    dataPath: "boardToolbar.style.subBackgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Font Color",
                    dataPath: "boardToolbar.style.fontColor",
                }
            ]
        },
        {
            name: "Page List",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Slider,
                    label: "Width",
                    dataPath: "pageList.width",
                    minValue: 200,
                    maxValue: 500,
                    stepValue: 5,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Page Selected Color",
                    dataPath: "pageList.pageSelectionColor",
                },
                {
                    type: SettingsType.Slider,
                    label: "Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "pageList.style.shadowLevel",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                },
                {
                    type: SettingsType.Group,
                    name: "List",
                    headerStyle: subGroupStyle,
                    elements: [
                        {
                            type: SettingsType.ColorPicker,
                            label: "Background Color",
                            dataPath: "pageList.style.backgroundColor",
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: "Font Color",
                            dataPath: "pageList.style.fontColor",
                        },
                    ]
                },
                {
                    type: SettingsType.Group,
                    name: "Toolbar",
                    headerStyle: subGroupStyle,
                    elements: [
                        {
                            type: SettingsType.ColorPicker,
                            label: "Background Color",
                            dataPath: "pageList.toolbarBackgroundColor",
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: "Font Color",
                            dataPath: "pageList.toolbarFontColor",
                        },
                    ]
                }
            ]
        },
        {
            name: "Generic Toolbars",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Slider,
                    label: "Height",
                    dataPath: "genericToolbars.height",
                    minValue: 20,
                    maxValue: 60,
                    stepValue: 5,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.Slider,
                    label: "Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "genericToolbars.style.shadowLevel",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Background Color",
                    dataPath: "genericToolbars.style.backgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    tooltip: "Sub-Background color. Helpful when we have secondary toolbar in the same context. (e.x. search bar)",
                    label: "SubBackground Color",
                    dataPath: "genericToolbars.style.subBackgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Font Color",
                    dataPath: "genericToolbars.style.fontColor",
                }
            ]
        },
        {
            name: "Boards Grid",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.ColorPicker,
                    label: "Main Background Color",
                    dataPath: "boardGrid.backgroundColor",
                },
                {
                    type: SettingsType.Group,
                    name: "Groups Toolbar",
                    dataPath: ".",
                    headerStyle: subGroupStyle,
                    elements: [
                        {
                            type: SettingsType.Slider,
                            label: "Height",
                            dataPath: "boardGrid.categoryToolbar.height",
                            minValue: 20,
                            maxValue: 60,
                            stepValue: 5,
                            valuePosfix: "px"
                        },
                        {
                            type: SettingsType.Slider,
                            label: "Shadow Level",
                            tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                            dataPath: "boardGrid.categoryToolbar.style.shadowLevel",
                            minValue: 0,
                            maxValue: 5,
                            stepValue: 1,
                            valuePosfix: ""
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: "Background Color",
                            dataPath: "boardGrid.categoryToolbar.style.backgroundColor",
                        },
                        {
                            type: SettingsType.ColorPicker,
                            tooltip: "Sub-Background color. Helpful when we have secondary toolbar in the same context. (e.x. search bar)",
                            label: "SubBackground Color",
                            dataPath: "boardGrid.categoryToolbar.style.subBackgroundColor",
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: "Font Color",
                            dataPath: "boardGrid.categoryToolbar.style.fontColor",
                        }
                    ]
                }

            ]
        },
        {
            name: "Dialog",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Slider,
                    label: "Toolbar Height",
                    dataPath: "dialog.height",
                    minValue: 20,
                    maxValue: 60,
                    stepValue: 5,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Toolbar Background Color",
                    dataPath: "dialog.toolbarBackgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Toolbar Font Color",
                    dataPath: "dialog.toolbarFontColor",
                },
                {
                    type: SettingsType.Slider,
                    label: "Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "dialog.shadowLevel",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                },
                {
                    type: SettingsType.Slider,
                    label: 'Border Radius',
                    tooltip: 'Defines the radius of the dialog\'s corners',
                    dataPath: 'dialog.borderRadius',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: 'px'
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Body Background Color",
                    dataPath: "dialog.backgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Body Font Color",
                    dataPath: "dialog.fontColor",
                }
            ]
        },
        {
            name: "Drawer",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Group,
                    name: "Toolbar",
                    headerStyle: subGroupStyle,
                    dataPath: ".",
                    elements: [
                        {
                            type: SettingsType.Slider,
                            label: "Shadow Level",
                            tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                            dataPath: "drawer.toolbarShadowLevel",
                            minValue: 0,
                            maxValue: 5,
                            stepValue: 1,
                            valuePosfix: ""
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: "Background Color",
                            dataPath: "drawer.toolbarBackgroundColor",
                        },
                        {
                            type: SettingsType.ColorPicker,
                            label: "Font Color",
                            dataPath: "drawer.toolbarFontColor",
                        }
                    ]
                },
                {
                    type: SettingsType.Slider,
                    label: "Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "drawer.shadowLevel",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                }
            ]
        },
        {
            name: "Page",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.ColorPicker,
                    label: "Canvas Background Color",
                    dataPath: "page.backgroundColor",
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Widget Background Color",
                    dataPath: "page.widgetBackgroundColor",
                },
                {
                    type: SettingsType.Slider,
                    label: "Widget Shadow Level",
                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                    dataPath: "page.zDepth",
                    minValue: 0,
                    maxValue: 5,
                    stepValue: 1,
                    valuePosfix: ""
                },
                {
                    type: SettingsType.Slider,
                    label: "Widget Border radius ",
                    dataPath: "page.borderRadius",
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: "px"
                },
            ]
        },
        {
            name: "Widgets",
            headerStyle: groupStyle,
            elements: [
                {
                    type: SettingsType.Group,
                    name: "Tree",
                    headerStyle: groupStyle,
                    dataPath: ".",
                    elements: [
                        {
                            type: SettingsType.Group,
                            name: "Toolbar",
                            dataPath: ".",
                            headerStyle: subGroupStyle,
                            elements: [
                                {
                                    type: SettingsType.Slider,
                                    label: "Shadow Level",
                                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                                    dataPath: "widgets.tree.toolbarShadowLevel",
                                    minValue: 0,
                                    maxValue: 5,
                                    stepValue: 1,
                                    valuePosfix: ""
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: "Background Color",
                                    dataPath: "widgets.tree.toolbarBackgroundColor",
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: "Font Color",
                                    dataPath: "widgets.tree.toolbarFontColor",
                                },
                            ]
                        },
                        {
                            type: SettingsType.Group,
                            name: "Searchbar",
                            dataPath: ".",
                            headerStyle: subGroupStyle,
                            elements: [
                                {
                                    type: SettingsType.Slider,
                                    label: "Shadow Level",
                                    tooltip: "Define the border shadow level (material-ui feeling). Shadow level 0 for no shadow.",
                                    dataPath: "widgets.tree.searchbarShadowLevel",
                                    minValue: 0,
                                    maxValue: 5,
                                    stepValue: 1,
                                    valuePosfix: ""
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: "Background Color",
                                    dataPath: "widgets.tree.searchbarBackgroundColor",
                                },
                                {
                                    type: SettingsType.ColorPicker,
                                    label: "Font Color",
                                    dataPath: "widgets.tree.searchbarFontColor",
                                },
                            ]
                        },
                        {
                            type: SettingsType.ColorPicker,
                            dataPath: "widgets.tree.treeBackgroundColor",
                            label: "Background Color"
                        },
                        {
                            type: SettingsType.ColorPicker,
                            dataPath: "widgets.tree.treeFontColor",
                            label: "Font Color"
                        },
                        {
                            type: SettingsType.ColorPicker,
                            dataPath: "widgets.tree.treeSelectColor",
                            label: "Item Select Color"
                        },
                        {
                            type: SettingsType.ColorPicker,
                            dataPath: "widgets.tree.treeDropdownTextIconColor",
                            label: "Dropdown button Text/Icon Color"
                        }
                    ]
                },
                {
                    type: SettingsType.Group,
                    name: "Radial Gauges",
                    headerStyle: groupStyle,
                    dataPath: ".",
                    elements: _.flatMap([
                        _.map([
                            { dataPath: 'widgets.radialGauge.colorPlate', label: 'Plate' },
                            { dataPath: 'widgets.radialGauge.colorPlateEnd', label: 'Plate End' },
                            { dataPath: 'widgets.radialGauge.colorMajorTicks', label: 'Major Ticks' },
                            { dataPath: 'widgets.radialGauge.colorMinorTicks', label: 'Minor Ticks' },
                            { dataPath: 'widgets.radialGauge.colorTitle', label: 'Title' },
                            { dataPath: 'widgets.radialGauge.colorUnits', label: 'Units' },
                            { dataPath: 'widgets.radialGauge.colorNumbers', label: 'Numbers' },
                            { dataPath: 'widgets.radialGauge.colorNeedle', label: 'Needle' },
                            { dataPath: 'widgets.radialGauge.colorNeedleEnd', label: 'Needle End' },
                            { dataPath: 'widgets.radialGauge.colorValueText', label: 'Value Text' },
                            { dataPath: 'widgets.radialGauge.colorValueTextShadow', label: 'Value TextShadow' },
                            { dataPath: 'widgets.radialGauge.colorBorderShadow', label: 'Border Shadow' },
                            { dataPath: 'widgets.radialGauge.colorBorderOuter', label: 'Border Outer' },
                            { dataPath: 'widgets.radialGauge.colorBorderOuterEnd', label: 'Border OuterEnd' },
                            { dataPath: 'widgets.radialGauge.colorBorderMiddle', label: 'Border Middle' },
                            { dataPath: 'widgets.radialGauge.colorBorderMiddleEnd', label: 'Border Middle End' },
                            { dataPath: 'widgets.radialGauge.colorBorderInner', label: 'Border Inner' },
                            { dataPath: 'widgets.radialGauge.colorBorderInnerEnd', label: 'Border Inner End' },
                            { dataPath: 'widgets.radialGauge.colorValueBoxRect', label: 'Value BoxRect' },
                            { dataPath: 'widgets.radialGauge.colorValueBoxRectEnd', label: 'Value BoxRect End' },
                            { dataPath: 'widgets.radialGauge.colorValueBoxBackground', label: 'Value Box Background' },
                            { dataPath: 'widgets.radialGauge.colorValueBoxShadow', label: 'Value Box Shadow' },
                            { dataPath: 'widgets.radialGauge.colorNeedleShadowUp', label: 'Needle ShadowUp' },
                            { dataPath: 'widgets.radialGauge.colorNeedleShadowDown', label: 'Needle ShadowDown' },
                            { dataPath: 'widgets.radialGauge.colorBarStroke', label: 'Bar Stroke' },
                            { dataPath: 'widgets.radialGauge.colorBar', label: 'Bar' },
                            { dataPath: 'widgets.radialGauge.colorBarProgress', label: 'Bar Progress' },
                            { dataPath: 'widgets.radialGauge.colorBarShadow', label: 'Bar Shadow' },
                        ], c => {
                            return {
                                type: SettingsType.ColorPicker,
                                label: c.label,
                                dataPath: c.dataPath
                            }
                        }),
                        [
                            {
                                type: SettingsType.Boolean,
                                label: 'Needle Shadow',
                                dataPath: 'widgets.radialGauge.needleShadow',
                                isNullable: true
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Needle Type',
                                dataPath: 'widgets.radialGauge.needleType',
                                isNullable: true,
                                items: [
                                    { label: "Arrow", value: "arrow" },
                                    { label: "Line", value: "line" },
                                ]
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Needle Start',
                                dataPath: 'widgets.radialGauge.needleStart',
                                minValue: 0,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: "%",
                                isNullable: true
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Needle End',
                                dataPath: 'widgets.radialGauge.needleEnd',
                                minValue: 0,
                                maxValue: 100,
                                stepValue: 1,
                                valuePosfix: "%",
                                isNullable: true
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Needle Width',
                                dataPath: 'widgets.radialGauge.needleWidth',
                                minValue: 2,
                                maxValue: 20,
                                stepValue: 1,
                                valuePosfix: "px",
                                isNullable: true
                            }
                        ]
                    ])
                },
                {
                    type: SettingsType.Group,
                    name: 'Charts',
                    headerStyle: groupStyle,
                    dataPath: '.',
                    elements: [
                        {
                            type: SettingsType.Icon,
                            label: 'Excel Icon',
                            dataPath: 'widgets.charts.excelIcon'
                        }
                    ]
                },
                {
                    type: SettingsType.Group,
                    name: 'Table',
                    headerStyle: groupStyle,
                    dataPath: '.',
                    elements: [
                        {
                            type: SettingsType.Icon,
                            label: 'Excel Icon',
                            dataPath: 'widgets.table.excelIcon'
                        }
                    ]
                }
            ]
        }
    ]
}
// ================================================================================================

export function GetDefaultBoardTheme(): BoardTheme {
    return {
        boardGrid: {
            backgroundColor: "#008c6a",
            categoryToolbar: {
                height: 48,
                style: {
                    fontColor: "white",
                    shadowLevel: 1,
                    backgroundColor: "rgb(19, 68, 89)",
                    subBackgroundColor: "rgb(19, 68, 89)"
                }
            }
        },
        genericToolbars: {
            height: 35,
            style: {
                shadowLevel: 2,
                fontColor: "white",
                backgroundColor: "rgb(19, 68, 89)",
                subBackgroundColor: "rgb(19, 68, 89)",
            }
        },

        appToolbar: {
            height: 35,
            style: {
                shadowLevel: 2,
                fontColor: "white",
                backgroundColor: "rgb(19, 68, 89)",
                subBackgroundColor: "rgb(19, 68, 89)",
            }
        },

        boardToolbar: {
            height: 35,
            fontSize: 15,
            style: {
                shadowLevel: 2,
                fontColor: "white",
                backgroundColor: "rgb(21, 84, 93)",
                subBackgroundColor: "rgb(21, 84, 93)",
            }
        },

        pageList: {
            width: 300,
            pageSelectionColor: "rgb(0, 157, 119)",
            toolbarFontColor: "white",
            toolbarBackgroundColor: "rgb(19, 68, 89)",
            style: {
                shadowLevel: 2,
                fontColor: "black",
                backgroundColor: "white",
                subBackgroundColor: "white",
            }
        },

        dialog: {
            height: 40,
            backgroundColor: "white",
            fontColor: "black",
            toolbarBackgroundColor: "rgb(19, 68, 89)",
            toolbarFontColor: "white",
            shadowLevel: 2,
            borderRadius: 10
        },

        drawer: {
            toolbarBackgroundColor: "rgb(19, 68, 89)",
            toolbarFontColor: "white",
            toolbarShadowLevel: 2,
            shadowLevel: 2
        },

        gemUserDialogBox: {
            bodyBackgroundColor: "white",
            toolbarBackgroundColor: "rgb(19, 68, 89)",
            toolbarFontColor: "white",
            toolbarShadowLevel: 1,
            acceptBackgroundColor: "#144459",
            acceptFontColor: "white",
            cancelBackgroundColor: "#D32F2F",
            cancelFontColor: "white"
        },

        widgets: {
            tree: {
                toolbarBackgroundColor: "rgb(19, 68, 89)",
                toolbarFontColor: "white",
                toolbarShadowLevel: 2,

                searchbarBackgroundColor: "rgb(19, 68, 89)",
                searchbarFontColor: "white",
                searchbarShadowLevel: 2,

                treeBackgroundColor: "white",
                treeFontColor: "black",
                treeSelectColor: "rgb(19, 68, 89)",
                treeDropdownTextIconColor: "black"
            },

            radialGauge: {
                // colors
                colorPlate: 'transparent',
                colorPlateEnd: '',
                colorMajorTicks: '#444',
                colorMinorTicks: '#666',
                colorStrokeTicks: '',
                colorTitle: '#888',
                colorUnits: '#888',
                colorNumbers: '#444',
                colorNeedle: 'rgba(240,128,128,1)',
                colorNeedleEnd: 'rgba(255,160,122,.9)',
                colorValueText: '#444',
                colorValueTextShadow: 'rgba(0,0,0,0.3)',
                colorBorderShadow: 'rgba(0,0,0,0.5)',
                colorBorderOuter: '#ddd',
                colorBorderOuterEnd: '#aaa',
                colorBorderMiddle: '#eee',
                colorBorderMiddleEnd: '#f0f0f0',
                colorBorderInner: '#fafafa',
                colorBorderInnerEnd: '#ccc',
                colorValueBoxRect: '#888',
                colorValueBoxRectEnd: '#666',
                colorValueBoxBackground: '#babab2',
                colorValueBoxShadow: 'rgba(0,0,0,1)',
                colorNeedleShadowUp: 'rgba(2,255,255,0.2)',
                colorNeedleShadowDown: 'rgba(188,143,143,0.45)',
                colorBarStroke: '#222',
                colorBar: '#ccc',
                colorBarProgress: '#888',
                colorBarShadow: '#000',

                // needle
                needleShadow: true,
                needleType: 'arrow',
                needleStart: 5,
                needleEnd: 85,
                needleWidth: 4
            },

            charts: {
                excelIcon: 'fas fa-file-excel'
            },

            table: {
                excelIcon: 'fas fa-file-excel'
            },

            settingsList: {
                toolbar: {
                    height: 30,
                    backgroundColor: "rgb(255, 158, 1)",
                    iconColor: "black",
                    shadowLevel: 2
                }
            }
        },

        page: {
            backgroundColor: "#008c6a",
            widgetBackgroundColor: "white",
            zDepth: 1,
            borderRadius: 15
        }
    }
}
// ================================================================================================
export class BoardTheming {
    // --------------------------------------------------------------------------------------------
    private static _instances: { [key: string]: BoardTheming } = {};
    private static _activeNodeKey: string;
    // --------------------------------------------------------------------------------------------
    public static GetInstance(nodeKey: string): BoardTheming { return (_.has(this._instances, nodeKey)) ? this._instances[nodeKey] : null; }
    public static GetTheme(nodeKey: string): BoardTheme {
        if (_.has(this._instances, nodeKey)) {
            return _.merge({}, GetDefaultBoardTheme(), this._instances[nodeKey].Theme);
        }
        // If we don't have the nodekey we giving the active one !!!
        return this.GetActiveTheme();
    }
    // --------------------------------------------------------------------------------------------
    public static SetTheme(nodeKey: string, theme: BoardTheme) {
        if (_.has(this._instances, nodeKey))
            this._instances[nodeKey].Theme = theme;
    }
    // --------------------------------------------------------------------------------------------
    public static GetActiveTheme(): BoardTheme {
        let defaultTheme = GetDefaultBoardTheme();
        if (_.has(this._instances, this._activeNodeKey))
            return _.merge({}, defaultTheme, this._instances[this._activeNodeKey].Theme);
        return defaultTheme;
    }
    // --------------------------------------------------------------------------------------------
    public static GetActiveNodeKey() { return this._activeNodeKey; }
    // --------------------------------------------------------------------------------------------
    public static SetActiveTheme(nodeKey: string) { this._activeNodeKey = nodeKey; }
    // --------------------------------------------------------------------------------------------

    // ============================================================================================

    public Theme: BoardTheme;
    public NodeKey: string;
    // --------------------------------------------------------------------------------------------
    constructor(nodeKey: string, theme: BoardTheme) {
        this.NodeKey = nodeKey;
        this.SetTheme(theme);

        // Update the instances
        BoardTheming._instances[nodeKey] = this;

        // If we don't have set any active nodekey set it
        if (BoardTheming._activeNodeKey == null)
            BoardTheming._activeNodeKey = nodeKey;
    }
    // --------------------------------------------------------------------------------------------
    public SetTheme(theme: BoardTheme) {
        // TODO: merge default values with it
        this.Theme = theme;
    }
    // --------------------------------------------------------------------------------------------
    public static getDashboardConfiguration(nodeKey: string, settings: DashboardConfiguration): DashboardConfiguration {
        settings = _.cloneDeep(settings);
        if (settings == null)
            settings = DashboardHelper.getDefaultConfiguration();

        const theme = this.GetTheme(nodeKey);
        if (!settings.OverrideCanvasBackgroundColor)
            settings.CanvasBackgroundColor = theme.page.backgroundColor;
        if (!settings.OverrideBorderRadius)
            settings.BorderRadius = theme.page.borderRadius;
        if (!settings.OverrideWidgetBackgroundColor)
            settings.WidgetBackgroundColor = theme.page.widgetBackgroundColor
        if (!settings.OverrideZDepth)
            settings.ZDepth = theme.page.zDepth;
        return settings;
    }
    // --------------------------------------------------------------------------------------------
}
// ================================================================================================